import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageMeta } from "@/components/common/PageMeta";
import { ScrollSection } from "@/components/main-page/ScrollSection";
import { AgentFactoryHero } from "@/components/products/AgentFactoryHero";
import { TabbedProductSection } from "@/components/products/TabbedProductSection";
import { XeniIngestion, XeniFormFill, XeniReview } from "@/components/products/mockups/XeniMockups";
import { RobolabSafety, RobolabPortals, RobolabCurriculum } from "@/components/products/mockups/RobolabMockups";
import { MetaroomUpload, MetaroomResearch, MetaroomCompliance } from "@/components/products/mockups/MetaroomMockups";

export function ProductsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const topnavHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--topnav-height") || "64", 10);
          const top = el.getBoundingClientRect().top + window.scrollY - topnavHeight - 16;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <>
      <PageMeta
        title="Products | COSX"
        description="XENI for immigration law. Robolab X for education. Metaroom for investment management. AI agents built for regulated industries on the COSX platform."
        path="/products"
      />

      <AgentFactoryHero />

      {/* XENI */}
      <TabbedProductSection
        id="xeni"
        title="XENI"
        oneLiner="AI paralegal for immigration law."
        badge={{ label: "Live", color: "text-emerald-500 bg-emerald-500/20" }}
        tractionLine="15 beta clients in the UK. ProActive Legal (AU/NZ) partner. 200+ pipeline cases."
        cta={{ label: "Register interest →", href: "/contact?interest=xeni" }}
        secondaryCta={{ label: "Visit XENI ↗", href: "https://xeni.legal", external: true }}
        features={[
          {
            title: "Document ingestion",
            text: "Upload client documents. XENI reads passports, bank statements, employment letters, and supporting evidence. It extracts every relevant data point automatically. No manual data entry.",
          },
          {
            title: "Form auto-completion",
            text: "XENI maps extracted data to UKVI form fields, filling in the application end-to-end. It cross-checks supporting evidence against Home Office requirements and flags any conflicts or missing documents before submission.",
          },
          {
            title: "Human QA and submission",
            text: "Every completed application goes through human quality assurance before submission. The lawyer reviews the AI's work, approves or corrects, and submits. A full audit trail records every decision for regulatory compliance.",
          },
        ]}
        mockupStates={[<XeniIngestion />, <XeniFormFill />, <XeniReview />]}
      />

      {/* Robolab X */}
      <TabbedProductSection
        id="robolabx"
        title="Robolab X"
        oneLiner="AI learning platform built for children."
        badge={{ label: "Launching", color: "text-purple-500 bg-purple-500/20" }}
        tractionLine="Exclusive school partnership. 20 pilot students. 2 classes in session."
        cta={{ label: "Register interest →", href: "/contact?interest=robolab-x" }}
        features={[
          {
            title: "Four-layer safety",
            text: "Every interaction passes through four safety layers before the student sees a response. Input filtering catches harmful queries. Keyword detection flags sensitive topics. A second AI audits the response for age-appropriateness. Output validation confirms the final answer is safe to deliver.",
          },
          {
            title: "Separate portals",
            text: "Students get an age-appropriate learning interface. Parents get a dashboard showing what their child is working on, how they're progressing, and what topics they've explored. School administrators get class-level analytics, content controls, and safeguarding reports.",
          },
          {
            title: "Curriculum-aligned content",
            text: "Robolab X content is aligned to the UK national curriculum. Students explore topics that map directly to their coursework. The platform is expanding to cover GCSE and A-level subjects, with learning paths designed by educators.",
          },
        ]}
        mockupStates={[<RobolabSafety />, <RobolabPortals />, <RobolabCurriculum />]}
      />

      {/* Metaroom */}
      <TabbedProductSection
        id="metaroom"
        title="Metaroom"
        oneLiner="AI-powered dataroom for investment management."
        badge={{ label: "Next", color: "text-amber-500 bg-amber-500/20" }}
        tractionLine="In planning. The COSX founding team built and exited a fintech agency. Metaroom applies that domain expertise to AI-native investment workflows."
        cta={{ label: "Register interest →", href: "/contact?interest=metaroom" }}
        features={[
          {
            title: "Document upload and AI analysis",
            text: "Drop your documents into Metaroom. Financial statements, share purchase agreements, due diligence checklists, term sheets. AI agents read, extract, and structure the data automatically. No manual tagging, no folder management.",
          },
          {
            title: "Portfolio research and benchmarking",
            text: "Metaroom's agents cross-reference extracted data against market benchmarks, comparable transactions, and regulatory requirements. Ask a question about the portfolio and get a sourced, auditable answer.",
          },
          {
            title: "Compliance review",
            text: "Every analysis is logged. Every data source is traceable. Metaroom runs compliance checks against the regulatory frameworks your fund operates under. Flagged items go to human review. Nothing leaves the platform without an audit trail.",
          },
        ]}
        mockupStates={[<MetaroomUpload />, <MetaroomResearch />, <MetaroomCompliance />]}
      />

      {/* Partner with us */}
      <ScrollSection id="partner" title="Partner with us" subtitle="We work with firms in regulated industries that want to deploy AI without compromising on compliance or oversight. If you're looking for a technology partner to help you scale, let's talk." compact>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/contact?interest=partnership"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity"
          >
            Get in touch
          </Link>
          <Link
            to="/platform"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium border border-[var(--color-border)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:border-[var(--color-fg-secondary)] transition-colors"
          >
            View the platform →
          </Link>
        </div>
      </ScrollSection>
    </>
  );
}
